import {
    type ComponentProps,
    type RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useScrollCarousel } from 'dibs-scroll-carousel/exports/ScrollCarouselContext.ts';
import { type InnerScrollCarousel } from 'dibs-scroll-carousel/exports/InnerScrollCarousel.ts';

import { getSrcsetUrl } from 'dibs-image-utils/exports/srcSet.ts';
import {
    trackArrowClick,
    trackViewRoomDisplayed,
} from '../../respShared/PdpRespSharedHeroCarousel/tracking.ts';
import { type CarouselItem } from './types.ts';
import { SRC_SET_WIDTHS } from './constants.ts';

export const THUMBS_VISIBLE = 7;
const THUMB_SIZE = 72;
const THUMB_GAP = 9;
const THUMB_ITEM_HEIGHT = THUMB_SIZE + THUMB_GAP;

type PreviewImage = { src: string; alt: string };

type ThumbsPanelReturn = {
    thumbsScrollRef: RefObject<HTMLDivElement | null>;
    canScrollUp: boolean;
    canScrollDown: boolean;
    scrollThumbsUp: () => void;
    scrollThumbsDown: () => void;
    hasOverflow: boolean;
    previewImage: PreviewImage | null;
    previewIndex: number | null;
    handleThumbHover: (index: number) => void;
    handleThumbHoverEnd: () => void;
    handleThumbSelect: (index: number) => void;
    onArrowClick: NonNullable<ComponentProps<typeof InnerScrollCarousel>['onArrowClick']>;
};

/**
 * Manages the vertical thumbnails panel that acts as a secondary controller
 * for the main hero carousel. Handles scroll position syncing, hover previews,
 * image preloading, and thumb click → main carousel navigation.
 *
 * The thumbs panel is a plain scrollable div (not an InnerScrollCarousel) because
 * it needs fine-grained control over scroll-into-view behavior, hover-triggered
 * preview overlays, and image preloading — none of which InnerScrollCarousel supports.
 */
export function useThumbsPanel({
    carouselItems,
    afterDisplay,
}: {
    carouselItems: CarouselItem[];
    afterDisplay: boolean;
}): ThumbsPanelReturn {
    const { activeSlide, totalSlides, setCurrentIndex } = useScrollCarousel();
    // Tracks whether the last slide change was triggered by a main carousel arrow click
    const slideChangeTypeRef = useRef<'next' | 'previous' | 'other'>('other');

    const [previewIndex, setPreviewIndex] = useState<number | null>(null);
    const preloadedUrlsRef = useRef<Set<string>>(new Set());

    const thumbsScrollRef = useRef<HTMLDivElement>(null);

    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(true);

    // Called by the main carousel's arrow buttons — records direction so the
    // activeSlide effect below can fire tracking and auto-scroll thumbs
    const onArrowClick: NonNullable<ComponentProps<typeof InnerScrollCarousel>['onArrowClick']> =
        useCallback(({ direction }) => {
            slideChangeTypeRef.current = direction;
        }, []);

    // When main carousel slide changes via arrow click, track it and
    // auto-scroll the thumbs panel so the active thumb stays visible
    useEffect(() => {
        const currentItem = carouselItems[activeSlide - 1];
        if (slideChangeTypeRef.current === 'next' || slideChangeTypeRef.current === 'previous') {
            trackArrowClick({
                slideType: currentItem.__type,
                activeSlide,
                totalSlides,
                arrowDirection: slideChangeTypeRef.current,
            });

            const el = thumbsScrollRef.current;
            if (el) {
                const thumbTop = (activeSlide - 1) * THUMB_ITEM_HEIGHT;
                const thumbBottom = thumbTop + THUMB_SIZE;
                const pageHeight = THUMB_ITEM_HEIGHT * THUMBS_VISIBLE;

                // Scroll thumbs panel if the active thumb is out of view
                if (thumbBottom > el.scrollTop + el.clientHeight || thumbTop < el.scrollTop) {
                    el.scrollTo({
                        top: Math.floor(thumbTop / pageHeight) * pageHeight,
                        behavior: 'smooth',
                    });
                }
            }
        }

        if (currentItem.__type === 'viewInRoom') {
            trackViewRoomDisplayed({ activeSlide, totalSlides });
        }

        slideChangeTypeRef.current = 'other';
    }, [activeSlide, totalSlides, carouselItems]);

    // Show/hide up/down scroll arrows based on current scroll position
    const updateScrollArrows = useCallback(() => {
        const el = thumbsScrollRef.current;
        if (!el) return;
        setCanScrollUp(el.scrollTop > 0);
        setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
    }, []);

    useEffect(() => {
        const el = thumbsScrollRef.current;
        if (!el) {
            return undefined;
        }
        updateScrollArrows();
        el.addEventListener('scroll', updateScrollArrows, { passive: true });
        return () => {
            el.removeEventListener('scroll', updateScrollArrows);
        };
    }, [updateScrollArrows, afterDisplay]);

    // Eagerly decode and cache an image URL so hover preview appears instantly.
    // Uses the srcSet-matching CDN URL so the preloaded image matches what the browser fetches.
    const preloadUrl = useCallback((url: string) => {
        if (!preloadedUrlsRef.current.has(url)) {
            const dpr = window.devicePixelRatio || 1;
            const width = dpr > 1 ? SRC_SET_WIDTHS[1] : SRC_SET_WIDTHS[0];
            const cdnUrl = getSrcsetUrl(url, width);
            const img = new window.Image();
            img.decoding = 'async';
            img.src = cdnUrl;
            preloadedUrlsRef.current.add(url);
        }
    }, []);

    const handleThumbHover = useCallback(
        (index: number) => {
            const hoveredItem = carouselItems[index];
            if (hoveredItem?.__type === 'photo' && hoveredItem.masterOrZoomPath) {
                preloadUrl(hoveredItem.masterOrZoomPath);
            }
            setPreviewIndex(index);
        },
        [carouselItems, preloadUrl]
    );

    const handleThumbHoverEnd = useCallback(() => {
        setPreviewIndex(null);
    }, []);

    // On click: navigate the main carousel to the selected thumb's index,
    // with backfill so infinite scrolling works correctly
    const handleThumbSelect = useCallback(
        (index: number) => {
            const bfCount = Math.min(carouselItems.length, THUMBS_VISIBLE);
            setCurrentIndex({
                index: index + bfCount,
                backfillStartCount: bfCount,
            });
        },
        [carouselItems.length, setCurrentIndex]
    );

    const scrollThumbsUp = useCallback(() => {
        const pageHeight = THUMB_ITEM_HEIGHT * THUMBS_VISIBLE;
        thumbsScrollRef.current?.scrollBy({ top: -pageHeight, behavior: 'smooth' });
    }, []);

    const scrollThumbsDown = useCallback(() => {
        const pageHeight = THUMB_ITEM_HEIGHT * THUMBS_VISIBLE;
        thumbsScrollRef.current?.scrollBy({ top: pageHeight, behavior: 'smooth' });
    }, []);

    const hasOverflow = carouselItems.length > THUMBS_VISIBLE;

    useEffect(() => {
        if (!afterDisplay) {
            return undefined;
        }

        const preloadPhoto = (item: CarouselItem): void => {
            if (item.__type === 'photo' && item.masterOrZoomPath) {
                preloadUrl(item.masterOrZoomPath);
            }
        };

        // Visible thumbs — user can hover these immediately
        carouselItems.slice(0, THUMBS_VISIBLE).forEach(preloadPhoto);

        // Off-screen thumbs — defer until browser is idle
        const remaining = carouselItems.slice(THUMBS_VISIBLE);
        if (remaining.length === 0) {
            return undefined;
        }

        const schedule = window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 200));
        const cancel = window.cancelIdleCallback ?? clearTimeout;
        const id = schedule(() => remaining.forEach(preloadPhoto));

        return () => cancel(id);
    }, [afterDisplay, carouselItems, preloadUrl]);

    // Resolve the hovered thumb index into an image to show as a preview overlay.
    // Returns null if hovering the already-active video (no need to overlay).
    const previewImage = useMemo((): PreviewImage | null => {
        if (previewIndex === null) {
            return null;
        }
        const item = carouselItems[previewIndex];
        if (item?.__type === 'photo' && item.masterOrZoomPath) {
            return { src: item.masterOrZoomPath, alt: item.alt || '' };
        }
        return null;
    }, [previewIndex, carouselItems]);

    return {
        thumbsScrollRef,
        canScrollUp,
        canScrollDown,
        scrollThumbsUp,
        scrollThumbsDown,
        hasOverflow,
        previewImage,
        previewIndex,
        handleThumbHover,
        handleThumbHoverEnd,
        handleThumbSelect,
        onArrowClick,
    };
}
